// BaseRailIcon.jsx

import React, { useRef, useState } from 'react';

// Context
import { useTheme } from 'context/ThemeContext';

// Utilities
import { PhotoShape } from 'utilities/Enums';
import { formatCount } from 'utilities/Formatters';

// CSS
import './BaseRailIcon.css';

// Components
import BaseCover from 'components/basecover/BaseCover';

/**
 * BaseRailIcon
 *
 * Renders a single icon in the desktop base rail.
 *
 * @returns {ReactNode} The rendered component.
 */
const BaseRailIcon = React.memo(({
    base,
    isActive = false,
    isDragging = false,
    isDropTarget = false,
    isExpired = false,
    isReorderEnabled = false,
    unreadCount = 0,
    onClick,
    onDragEnd,
    onDragOver,
    onDragStart,
    onDrop,
}) => {

    // ----- CONTEXT ----- //

    const { theme } = useTheme();

    // ----- STATE ----- //

    const [hovered, setHovered] = useState(false);
    // ----- REFS ----- //

    const didDragRef = useRef(false);

    const title = base?.title || 'Space';
    const badgeLabel = unreadCount > 99 ? '99+' : formatCount(unreadCount);
    const reorderTitle = isReorderEnabled ? 'Drag to reorder' : title;

    // ----- HANDLERS ----- //

    /**
     * handleClick
     *
     * Handles a click.
     */
    const handleClick = () => {
        if (didDragRef.current) {
            didDragRef.current = false;
            return;
        }

        onClick?.();
    };

    /**
     * handleDragStart
     *
     * Handles drag start.
     *
     * @param {object} event - event.
     */
    const handleDragStart = (event) => {
        if (!isReorderEnabled) {
            event.preventDefault();
            return;
        }

        didDragRef.current = true;
        onDragStart?.(event);
    };

    /**
     * handleDragEnd
     *
     * Handles drag end.
     *
     * @param {object} event - event.
     */
    const handleDragEnd = (event) => {
        window.setTimeout(() => {
            didDragRef.current = false;
        }, 0);
        onDragEnd?.(event);
    };

    // ----- RENDER ----- //

    return (
        <button
          className={[
                'base-rail-icon-container',
                isDragging ? 'base-rail-icon-container-dragging' : '',
                isDropTarget ? 'base-rail-icon-container-drop-target' : '',
                isReorderEnabled ? 'base-rail-icon-container-sortable' : '',
            ].filter(Boolean).join(' ')}
          type="button"
          aria-current={isActive ? 'true' : undefined}
          aria-label={unreadCount > 0 ? `${title}, ${badgeLabel} unread notifications` : title}
          title={isExpired ? `${title} (Expired)` : reorderTitle}
          draggable={isReorderEnabled}
          onClick={handleClick}
          onDragEnd={handleDragEnd}
          onDragOver={onDragOver}
          onDragStart={handleDragStart}
          onDrop={onDrop}
          onMouseEnter={() => setHovered(true)}
          onMouseLeave={() => setHovered(false)}
        >

            {/* ACTIVE INDICATOR */}
            {isActive && (
                <span
                  className="base-rail-icon-active-indicator"
                  aria-hidden="true"
                />
            )}

            {/* HOVER */}
            <span
              className="base-rail-icon-hover"
              style={{
                    backgroundColor: hovered ? theme.buttonBackgroundColor : 'transparent',
                    opacity: hovered ? 1 : 0,
                }}
              aria-hidden="true"
            />

            {/* VISUAL */}
            <span className="base-rail-icon-visual">
                <BaseCover
                  base={base}
                  assetType="switcher"
                  borderWidth={0}
                  borderRadius={8}
                  size={40}
                  shape={PhotoShape.SQUARE}
                />

                {/* NOTIFICATION BADGE */}
                {unreadCount > 0 && (
                    <span
                      className="base-rail-icon-notification-badge"
                      style={{
                        border: `2px solid ${theme.backgroundMuted}`,
                        color: '#FFFFFF',
                      }}
                    >
                        {badgeLabel}
                    </span>
                )}

                {/* EXPIRED BADGE */}
                {isExpired && (
                    <span
                      className="base-rail-icon-expired-badge"
                      aria-hidden="true"
                    />
                )}
            </span>
        </button>
    );
});

// Dev Tools
BaseRailIcon.displayName = 'BaseRailIcon';

export default BaseRailIcon;
